import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Button, Col, Form, Row, Table, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SelectSearch from "react-select-search";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { adminGetAllUsers } from "../actions/userActions";
import "../dashboard.css";
import { useHistory } from "react-router-dom";
import crypto from "crypto";
import useIsMobile from "../utils/useIsMobile";
import FormField from "../components/FormField";
import NoKeyboardInput from "../components/NoKeyboardInput";
import SegmentedControl from "../components/SegmentedControl";
import LoaderButton from "../components/LoaderButton";

const regexNum = /^\d+$/;
const phoneNum = /^(\+91[-\s]?|91[-\s]?|0)?([6-9]\d{9})$/;
const regexText = /^(\w+\s?)*\s*$/;

const subName = ["Mr.", "Mrs.", "Miss.", "Dr/Mr.", "Dr/Mrs.", "Dr/Miss.", "Baby.", "Master.", "B/O", "Others."];

const format_rupees = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(val);

const Billing = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userLogin.userInfo);
  const { users: adminUsers } = useSelector((state) => state.adminUsers || { users: [] });
  const isAdmin = userInfo && userInfo.role === "admin";

  // State
  const [invoiceNo, setInvoiceNo] = useState(null);
  const [billDate, setBillDate] = useState(new Date());
  const [patientSubName, setPatientSubName] = useState("");
  const [patientName, setPatientName] = useState("");
  const [refDoc, setRefDoc] = useState("");
  const [ptID, setPTID] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [paymentType, setPaymentType] = useState("Cash");
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [validEmail, setValidEmail] = useState(true);
  const [ageYear, setAgeYear] = useState("");
  const [ageMonth, setAgeMonth] = useState("");
  const [trfType, setTrfType] = useState("No");
  const [accessionNos, setAccessionNos] = useState([""]);

  const [total, setTotal] = useState(0);
  const [less, setLess] = useState(0);
  const [netAmount, setNetAmount] = useState(0);
  const [advance, setAdvance] = useState(0);
  const [balancePayable, setBalancePayable] = useState(0);
  const [billingJson, setBillingJson] = useState([{ description: "", amount: "" }]);
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobilePop, setMobilePop] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, resolve: null });
  const [oldRecords, setOldRecords] = useState([]);
  const isMobile = useIsMobile();
  const [selectedCompany, setSelectedCompany] = useState(userInfo.company || "");
  const [assignedUser, setAssignedUser] = useState("");

  // Refs for mutable values
  const testsListRef = useRef([]);
  const listTestCodesRef = useRef([]);
  const stateEditRef = useRef(false);
  const isBookingRef = useRef(false);
  const paramIdRef = useRef("");
  const oldSearchValueRef = useRef("");
  const oldTestOptionsRef = useRef([]);

  // Helper: Axios config
  const axiosConfig = useMemo(
    () => ({
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.token}`,
      },
    }),
    [userInfo.token]
  );

  // Generate unique invoice number
  const checkInvoiceNumber = useCallback(async () => {
    let nextInvoice = crypto.randomBytes(4).toString("hex");
    try {
      const res = await axios.get(`/api/v1/bill/checkBillNo/${nextInvoice}`, axiosConfig);
      if (res.data.valid) {
        setInvoiceNo(nextInvoice);
      } else {
        checkInvoiceNumber();
      }
    } catch { }
  }, [axiosConfig]);

  // Fetch invoice data
  const getDatafromInvoice = useCallback(
    async (invId) => {
      let endpoint = isBookingRef.current ? "booking/view" : "bill/viewInvoice";
      try {
        const res = await axios.get(`/api/v1/${endpoint}/${invId}`, axiosConfig);
        if (res.data.data) {
          stateEditRef.current = !isBookingRef.current;
          const invData = res.data.data;
          const patName = invData.patient_name.includes(".")
            ? invData.patient_name.split(".")
            : invData.patient_name;
          if (!isBookingRef.current) setInvoiceNo(invData.invoice_no);
          setPatientSubName(invData.patient_name.includes(".") ? patName[0] + "." : "");
          setPatientName(invData.patient_name.includes(".") ? patName[1].trim() : invData.patient_name);
          setBillDate(new Date(isBookingRef.current ? invData.booking_date : invData.invoice_date));
          setAge(invData.patient_age);
          setGender(invData.patient_gender);
          setRefDoc(invData.reference_doctor);
          setMobileNumber(invData.patient_mobile);
          setPTID(invData.pt_id);
          try { setAccessionNos(invData.accession_nos ? JSON.parse(invData.accession_nos) : [""]); } catch { setAccessionNos([""]); }
          setEmail(invData.patient_email);
          setPaymentType(invData.payment_type);
          setAdvance(invData.advance);
          setLess(invData.discounts);
          setNetAmount(invData.net_amount);
          setTotal(Math.floor(invData.net_amount + invData.discounts));
          setBalancePayable(Math.floor(invData.net_amount - invData.advance));
          setBillingJson(JSON.parse(invData.items));
          let fullage = invData.patient_age.includes(".") ? invData.patient_age.split(".") : null;
          if (fullage) {
            setAgeYear(fullage[0]);
            setAgeMonth(fullage[1]);
          } else {
            setAgeYear(invData.patient_age);
          }
          let tempItems = JSON.parse(invData.items);
          listTestCodesRef.current = tempItems.map((element) => element.description.split(" - ")[0]);

          if (isAdmin) {
            setSelectedCompany(invData.company || "");
            setAssignedUser(invData.createdBy || "");
          }
        }
      } catch { }
    },
    [axiosConfig, isAdmin]
  );

  // Search mobile data
  const searchMobileData = useCallback(
    async (mobile) => {
      try {
        const res = await axios.post(
          `/api/v1/bill/checkMobileNumber`,
          { mobile_number: mobile },
          axiosConfig
        );
        if (res.data.data && res.data.data.length > 0) {
          setOldRecords(res.data.data);
          setMobilePop(true);
        }
      } catch { }
    },
    [axiosConfig]
  );

  // Handle URL params and invoice loading
  useEffect(() => {
    const search = window.location.search;
    if (search && (search.includes("trfNo") || search.includes("id"))) {
      if (search.includes("id")) {
        checkInvoiceNumber();
        isBookingRef.current = true;
      }
      const query = search.split("=")[1];
      paramIdRef.current = query;
      getDatafromInvoice(query);
    } else {
      if (stateEditRef.current) window.location.reload();
      checkInvoiceNumber();
      stateEditRef.current = false;
      isBookingRef.current = false;
      paramIdRef.current = "";
    }
    // eslint-disable-next-line
  }, [window.location.search, checkInvoiceNumber, getDatafromInvoice]);

  // Fetch labs for admin
  useEffect(() => {
    if (isAdmin) dispatch(adminGetAllUsers());
  }, [isAdmin, dispatch]);

  // Calculate totals when billingJson changes
  useEffect(() => {
    const sum = billingJson.reduce((acc, curr) => acc + parseInt(curr.amount || 0), 0);
    setTotal(sum);
    setNetAmount(sum - less);
    setBalancePayable(sum - less - advance);
    // eslint-disable-next-line
  }, [billingJson, less, advance]);

  // Change amount for a test
  const handleChangeAmount = useCallback(
    (value, index) => {
      if (!isNaN(value)) {
        value = value !== "" ? +value : 0;
        setBillingJson((prev) => {
          const arrObj = [...prev];
          arrObj[index] = { ...arrObj[index], amount: value };
          return arrObj;
        });
      }
    },
    []
  );

  // Change billing description
  const changeBillingDescription = useCallback(
    (value, index) => {
      let tempValue = value.split(" - ");
      let testItem = testsListRef.current.find(({ test_code }) => test_code === tempValue[0]);
      setBillingJson((prev) => {
        const arrObj = [...prev];
        arrObj[index] = { ...arrObj[index], description: value };
        return arrObj;
      });
      handleChangeAmount(testItem?.test_price || 0, index);
      listTestCodesRef.current.push(tempValue[0]);
    },
    [handleChangeAmount]
  );

  // Billing description search (debounced)
  const searchBillingDescription = useCallback(
    async (value, defaultOptions = []) => {
      if (!value || value.length <= 2) {
        return defaultOptions;
      }
      if (value === oldSearchValueRef.current) {
        return oldTestOptionsRef.current;
      }
      try {
        const res = await axios.get(`/api/v1/tests/list/${value}`, axiosConfig);
        oldSearchValueRef.current = value;
        if (res.data.data) {
          testsListRef.current = res.data.data;
          const mapped = res.data.data
            .filter((o1) => o1.test_code)
            .map((o1) => ({
              name: o1.test_code + " - " + o1.test_name,
              value: o1.test_code + " - " + o1.test_name,
            }));
          oldTestOptionsRef.current = mapped;
          return mapped;
        }
      } catch { }
      return defaultOptions;
    },
    [axiosConfig]
  );

  // Add new line to billing
  const addNewLine = useCallback(() => {
    setBillingJson((prev) => [...prev, { description: "", amount: "" }]);
  }, []);

  // Delete a line from billing
  const deleteLine = useCallback((index) => {
    setBillingJson((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Delete booking
  const deleteBooking = useCallback(async () => {
    try {
      await axios.delete(`/api/v1/booking/delete/${paramIdRef.current}`, axiosConfig);
    } catch { }
  }, [axiosConfig]);

  // Generate PDF
  const generatePdf = useCallback(async () => {
    try {
      const res = await axios.get(
        `/api/v1/bill/generatePdf/${invoiceNo + "&" + trfType}`,
        axiosConfig
      );
      if (res.data.pdf) {
        var inLink = document.createElement("a");
        inLink.download = invoiceNo + ".pdf";
        inLink.href = "data:application/octet-stream;base64," + res.data.pdf;
        document.body.appendChild(inLink);
        inLink.click();
        document.body.removeChild(inLink);
        if (!stateEditRef.current) {
          history.push("/billing");
          window.location.reload();
        }
      }
    } catch { }
  }, [invoiceNo, trfType, axiosConfig, history]);

  // Update old details
  const updateOldDetails = (invData) => {
    const patName = invData.patient_name.includes(".")
      ? invData.patient_name.split(".")
      : invData.patient_name;
    setPatientSubName(invData.patient_name.includes(".") ? patName[0] + "." : "");
    setPatientName(invData.patient_name.includes(".") ? patName[1].trim() : invData.patient_name);
    setAge(invData.patient_age);
    setGender(invData.patient_gender);
    setRefDoc(invData.reference_doctor);
    setMobileNumber(invData.patient_mobile);
    setEmail(invData.patient_email);
    setMobilePop(false);
    let fullage = invData.patient_age.includes(".") ? invData.patient_age.split(".") : null;
    if (fullage) {
      setAgeYear(fullage[0]);
      setAgeMonth(fullage[1]);
    } else {
      setAgeYear(invData.patient_age);
    }
  };

  // Update patient sub name and gender
  const updatePatientSubName = (e) => {
    setPatientSubName(e.target.value);
    if (e.target.value === "Mr." || e.target.value === "Dr/Mr.") {
      setGender("Male");
    } else if (
      e.target.value === "Mrs." ||
      e.target.value === "Miss." ||
      e.target.value === "Dr/Mrs." ||
      e.target.value === "Dr/Miss."
    ) {
      setGender("Female");
    } else {
      setGender("");
    }
  };

  // Copy barcode
  const copyBarcode = async () => {
    try {
      if (listTestCodesRef.current.length > 0) {
        await navigator.clipboard.writeText(listTestCodesRef.current.join(","));
        alert("Test codes copied to clipboard");
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  // Handle form submit
  const handleAccessionNoChange = useCallback((value, index) => {
    setAccessionNos((prev) => {
      const arr = [...prev];
      arr[index] = value;
      return arr;
    });
  }, []);

  const addAccessionNoField = useCallback(() => {
    if (accessionNos.length < 4 && accessionNos[accessionNos.length - 1].trim() !== "") {
      setAccessionNos((prev) => [...prev, ""]);
    }
  }, [accessionNos]);

  const removeAccessionNo = useCallback((index) => {
    setAccessionNos((prev) => {
      const arr = prev.filter((_, i) => i !== index);
      return arr.length === 0 ? [""] : arr;
    });
  }, []);

  const confirmDownload = () => {
    return new Promise((resolve) => {
      setConfirmModal({ show: true, resolve });
    });
  };

  const handleConfirmAction = (choice) => {
    if (confirmModal.resolve) {
      confirmModal.resolve(choice);
    }
    setConfirmModal({ show: false, resolve: null });
  };

  const handleSubmit = useCallback(async () => {
    if (patientSubName && patientName && age && billDate && gender && mobileNumber && validEmail) {
      setIsSubmitting(true);
      let data = {
        invoiceNo,
        invoice_date: billDate,
        patient_name: patientSubName + " " + patientName,
        patient_age: age,
        patient_gender: gender,
        patient_mobile: mobileNumber,
        patient_email: email,
        paymentType,
        reference_doctor: refDoc,
        pt_id: ptID,
        accession_nos: accessionNos.filter(a => a.trim() !== "").length > 0 ? accessionNos.filter(a => a.trim() !== "") : null,
        items: billingJson,
        discount: less,
        advance: advance,
        net_amount: netAmount,
        ...(isAdmin ? {
          company: selectedCompany,
          userId: assignedUser
        } : {}),
      };
      let endpoint = stateEditRef.current ? "update" : "create";
      try {
        const res = await axios.post(`/api/v1/bill/${endpoint}`, data, axiosConfig);
        if (res.status === 200) {
          if (isBookingRef.current) {
            await deleteBooking();
          }
          // if (stateEditRef.current) {
          const wantsDownload = await confirmDownload();
          if (wantsDownload) {
            await generatePdf();
          }
          // } else {
          //   await generatePdf();
          // }

          if (!stateEditRef.current) {
            history.push("/billing");
            window.location.reload();
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsError(true);
      alert("Please enter all mandatory fields");
    }
  }, [
    patientSubName,
    patientName,
    age,
    billDate,
    gender,
    mobileNumber,
    invoiceNo,
    email,
    paymentType,
    refDoc,
    ptID,
    billingJson,
    less,
    advance,
    netAmount,
    axiosConfig,
    deleteBooking,
    generatePdf,
    validEmail,
    isAdmin,
    selectedCompany,
    assignedUser,
    accessionNos,
    history
  ]);

  // Change less amount
  const changeLessAmount = (e) => {
    const val = e.target.value;
    if (val !== "" && val <= total && regexNum.test(val)) {
      setNetAmount(total - parseInt(val));
      let payable = total - parseInt(val) - advance;
      setBalancePayable(payable < 0 ? 0 : payable);
      setLess(val);
    } else if (val === "") {
      setNetAmount(total);
      setBalancePayable(total - advance);
      setLess(0);
    }
  };

  // Change advance amount
  const changeAdvanceAmount = (e) => {
    const val = e.target.value;
    if (val !== "" && val <= netAmount && regexNum.test(val)) {
      setBalancePayable(total - less - parseInt(val));
      setAdvance(val);
    } else if (val === "") {
      setBalancePayable(netAmount);
      setAdvance(0);
    }
  };

  const isEditMode = window.location.search.includes('trfNo');
  const editTrfNo = isEditMode ? window.location.search.split('=')[1] : '';

  return (
    <div className="billing-screen pb-5">
      {/* Header */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h4 className="font-weight-bold grey-900 mb-1" style={{ letterSpacing: '-0.5px', fontSize: 'clamp(1rem, 4vw, 1.4rem)' }}>
              Test Requisition Form (TRF)
            </h4>
            <p className="text-muted mb-0 small">
              <i className={`fa ${isEditMode ? 'fa-pencil-square text-warning' : 'fa-plus-circle text-primary'} mr-1`}></i>
              {isEditMode
                ? <>Editing: <strong>#{editTrfNo}</strong></>
                : 'Preparing new test requisition'}
            </p>
          </div>
          <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
            {isEditMode ? (
              <Button
                variant="success"
                onClick={() => history.push('/billing')}
                style={{ fontWeight: 600, height: '36px', padding: '0 14px', borderRadius: '8px', fontSize: '0.85rem' }}
              >
                <i className="fa fa-plus mr-1"></i>New TRF
              </Button>
            ) : (
              <div className="text-right">
                <span className="small text-muted text-uppercase font-weight-bold d-block" style={{ fontSize: '0.6rem' }}>Invoice No.</span>
                <span className="badge badge-light p-1 font-weight-bold" style={{ fontSize: '0.85rem', border: '1px solid #e2e8f0' }}>#{invoiceNo}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Admin: User Selector */}
      {isAdmin && (
        <div className="billing-form-card custom-card-boxshadow mb-4 p-4" style={{ borderRadius: '20px' }}>
          <h5 className="billing-section-title mb-3 font-weight-bold d-flex align-items-center">
            <span className="section-number-icon mr-3"><i className="fa fa-user"></i></span>
            Select User
          </h5>
          <Row>
            <Col xs={12} md={6}>
              <FormField label="Assign to User" mandatory error={isAdmin && !assignedUser}>
                <Form.Control
                  as="select"
                  className="bg-light border-0"
                  value={assignedUser}
                  onChange={(e) => {
                    const selVal = e.target.value;
                    setAssignedUser(selVal);
                    const tUser = adminUsers.find(u => String(u.user_id) === String(selVal));
                    if (tUser) setSelectedCompany(tUser.company);
                    else setSelectedCompany("");
                  }}
                >
                  <option value="">-- Select a User --</option>
                  {adminUsers && adminUsers.filter(u => u.role !== 'admin').map((u) => (
                    <option key={u.id} value={u.user_id}>{u.user_name}</option>
                  ))}
                </Form.Control>
              </FormField>
            </Col>
          </Row>
        </div>
      )}

      {/* Section 1: Patient Information */}
      <div className="billing-form-card custom-card-boxshadow mb-4">
        <h5 className="billing-section-title mb-4 font-weight-bold">1. Patient Information</h5>
        <Row>
          <Col xs={12} sm={6} md={3}>
            <FormField label="Mobile Number" mandatory error={isError && !mobileNumber}>
              <Form.Control
                maxLength={13}
                className="bg-light border-0"
                value={mobileNumber}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const cleanedValue = rawValue.replaceAll(/[^\d+\s]/g, '').trim();
                  setMobileNumber(cleanedValue);
                  const match = cleanedValue.match(phoneNum);
                  if (match) {
                    const tenDigitNumber = match[2];
                    if (tenDigitNumber.length === 10) searchMobileData(tenDigitNumber);
                  }
                }}
                type="tel" placeholder="Ex: 9876543210"
              />
            </FormField>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <FormField label="Patient Name" mandatory error={isError && (!patientSubName || !patientName)}>
              <div className="d-flex" style={{ gap: '8px' }}>
                <Form.Control
                  className="bg-light border-0 w-auto"
                  as="select" value={patientSubName} onChange={(e) => updatePatientSubName(e)}
                >
                  <option hidden>Title</option>
                  {subName.map((o1, index) => <option value={o1} key={index}>{o1}</option>)}
                </Form.Control>
                <Form.Control
                  className="bg-light border-0 flex-grow-1"
                  value={patientName}
                  onChange={(e) => {
                    if (regexText.test(e.target.value)) setPatientName(e.target.value);
                  }}
                  type="text" placeholder="Full Name"
                />
              </div>
            </FormField>
          </Col>
          <Col xs={6} sm={3} md={2}>
            <FormField label="Age (Y/M)" mandatory error={isError && !age}>
              <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                <Form.Control
                  maxLength={2} className="bg-light border-0 text-center"
                  value={ageYear} onChange={(e) => {
                    if (!e.target.value || regexNum.test(e.target.value)) {
                      setAgeYear(e.target.value); setAge(e.target.value + (ageMonth ? "." + ageMonth : ""));
                    }
                  }} placeholder="Y"
                />
                <span className="text-muted font-weight-bold">/</span>
                <Form.Control
                  maxLength={2} className="bg-light border-0 text-center"
                  value={ageMonth} onChange={(e) => {
                    if (!e.target.value || regexNum.test(e.target.value)) {
                      setAgeMonth(e.target.value); setAge((ageYear || "0") + "." + e.target.value);
                    }
                  }} placeholder="M"
                />
              </div>
            </FormField>
          </Col>
          <Col xs={12} sm={9} md={3}>
            <FormField label="Gender" mandatory error={isError && !gender}>
              <SegmentedControl
                options={["Male", "Female", "Other"]}
                value={gender}
                onChange={setGender}
              />
            </FormField>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={6} md={3}>
            <FormField label="Investigation Date">
              <DatePicker
                placeholderText="Select Date & Time"
                className="form-control bg-light border-0"
                selected={billDate}
                onChange={(date) => setBillDate(date)}
                maxDate={new Date()}
                showTimeSelect
                timeIntervals={15}
                timeCaption="Time"
                portalId="root-portal"
                popperPlacement="bottom-start"
                popperModifiers={[{ name: "preventOverflow", options: { boundary: "viewport" } }]}
                dateFormat="dd/MM/yyyy h:mm aa"
                withPortal={isMobile}
                customInput={isMobile ? <NoKeyboardInput className="form-control bg-light border-0" /> : undefined}
              />
            </FormField>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <FormField label="Reference Doctor">
              <Form.Control className="bg-light border-0" value={refDoc} onChange={(e) => setRefDoc(e.target.value)} type="text" placeholder="Dr. Name" />
            </FormField>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <FormField label="Email Address" error={!validEmail && "Invalid email address"}>
              <Form.Control
                className="bg-light border-0"
                value={email ?? ""} onChange={(e) => { setEmail(e.target.value); setValidEmail(true); }}
                onBlur={() => { if (email && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)) setValidEmail(false); }}
                type="email" placeholder="patient@email.com"
              />
            </FormField>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <FormField label="Payment Mode">
              <SegmentedControl
                options={["Cash", "Card", "UPI"]}
                value={paymentType}
                onChange={setPaymentType}
              />
            </FormField>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12}>
            <FormField label="Accession Numbers">
              <Row>
                {accessionNos.map((acc, index) => (
                  <Col xs={6} sm={4} md={3} key={index} className="mb-2">
                    <div className="position-relative">
                      <Form.Control
                        type="text"
                        className="bg-light border-0 text-uppercase"
                        style={{ paddingRight: '30px', height: '45px' }}
                        value={acc}
                        maxLength={12}
                        onChange={(e) => handleAccessionNoChange(e.target.value.toUpperCase(), index)}
                        placeholder={`Accession ${index + 1}`}
                      />
                      {accessionNos.length > 1 && (
                        <Button
                          variant="link"
                          className="position-absolute p-0 text-danger"
                          style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                          onClick={() => removeAccessionNo(index)}
                        >
                          <i className="fa fa-times"></i>
                        </Button>
                      )}
                    </div>
                  </Col>
                ))}
                {accessionNos.length < 4 && accessionNos[accessionNos.length - 1].trim() !== "" && (
                  <Col xs={6} sm={4} md={3} className="mb-2">
                    <Button
                      variant="outline-primary"
                      className="w-100 bg-white text-primary font-weight-bold d-flex align-items-center justify-content-center"
                      onClick={addAccessionNoField}
                      style={{ border: '1px dashed #0d6efd', borderRadius: '4px', height: '45px' }}
                    >
                      <i className="fa fa-plus mr-1"></i> Add
                    </Button>
                  </Col>
                )}
              </Row>
            </FormField>
          </Col>
        </Row>
      </div>

      {/* Section 2: Investigations */}
      <div className="billing-form-card custom-card-boxshadow mb-4">
        <h5 className="billing-section-title mb-4">2. Selected Investigations</h5>
        <div className="investigations-container py-1">
          {/* Desktop Table: Hidden on Mobile & Tablets */}
          <div className="d-none d-md-block table-responsive border rounded-lg mb-4">
            <Table hover size="sm" className="mb-0 border-0">
              <thead className="bg-light">
                <tr>
                  <th className="text-center py-3 border-0" width="60">#</th>
                  <th className="py-3 border-0">Name of the Test</th>
                  <th className="py-3 border-0" width="160">Amount</th>
                  <th className="text-center py-3 border-0" width="100">Actions</th>
                </tr>
              </thead>
              <tbody>
                {billingJson.map((val, i) => (
                  <tr key={i} className="test-row-hover">
                    <td className="text-center align-middle border-bottom font-weight-bold">{i + 1}</td>
                    <td className="p-0 border-bottom">
                      <div className="select-search-premium-wrapper">
                        <SelectSearch
                          search
                          options={val.description ? [{ name: val.description, value: val.description }] : []}
                          value={val.description} placeholder="Search investigations..."
                          getOptions={searchBillingDescription} onChange={(e) => changeBillingDescription(e, i)}
                        />
                      </div>
                    </td>
                    <td className="p-0 border-bottom">
                      <Form.Control
                        type="number" className="border-0 bg-transparent font-weight-bold py-3"
                        value={val.amount} onChange={(e) => handleChangeAmount(e.target.value, i)}
                      />
                    </td>
                    <td className="text-center align-middle border-bottom" style={{ width: '100px' }}>
                      <div className="d-flex justify-content-center align-items-center" style={{ gap: '4px' }}>
                        <Button
                          variant="link"
                          className="row-action-btn add-btn"
                          onClick={() => addNewLine()}
                          disabled={val.description === "" || val.amount === 0}
                          title="Add row"
                          style={{ visibility: billingJson.length === i + 1 ? 'visible' : 'hidden' }}
                        >
                          <i className="fa fa-plus-circle" style={{ fontSize: '28px' }}></i>
                        </Button>
                        <Button
                          variant="link"
                          className="row-action-btn remove-btn"
                          onClick={() => deleteLine(i)}
                          title="Remove row"
                          style={{ visibility: billingJson.length > 1 ? 'visible' : 'hidden' }}
                        >
                          <i className="fa fa-times-circle" style={{ fontSize: '28px' }}></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile Dense Layout: Compact and Clean (Visible on Mobile & Tablets < 768px) */}
          <div className="d-block d-md-none">
            {billingJson.map((val, i) => (
              <div key={i} className="investigation-row-dense">
                <div className="d-flex align-items-center">
                  <span className="item-index-small mr-2">{i + 1}</span>
                  <div className="flex-grow-1 select-search-premium-wrapper">
                    <SelectSearch
                      search
                      options={val.description ? [{ name: val.description, value: val.description }] : []}
                      value={val.description} placeholder="Search investigations..."
                      getOptions={searchBillingDescription} onChange={(e) => changeBillingDescription(e, i)}
                    />
                  </div>
                  {billingJson.length > 1 && (
                    <Button variant="link" className="p-2 text-danger ml-2" onClick={() => deleteLine(i)}>
                      <i className="fa fa-times-circle-o fa-lg"></i>
                    </Button>
                  )}
                </div>

                <div className="amount-input-dense mt-1">
                  <span>₹</span>
                  <Form.Control
                    type="number"
                    className="border-0 bg-transparent font-weight-bold p-0"
                    value={val.amount}
                    onChange={(e) => handleChangeAmount(e.target.value, i)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            ))}

            <div className="mobile-add-btn-container mb-3">
              <Button
                variant="outline-primary"
                className="btn-add-test-mobile py-2"
                onClick={() => addNewLine()}
                disabled={billingJson[billingJson.length - 1].description === "" || billingJson[billingJson.length - 1].amount === 0}
              >
                <i className="fa fa-plus"></i> Add Test Line
              </Button>
            </div>
          </div>
        </div>

        <Row>
          <Col lg={7}>
            <Form.Group className="mb-3">
              <Form.Label className="small font-weight-bold text-muted text-uppercase letter-spacing-1">Note / Remarks</Form.Label>
              <Form.Control as="textarea" rows={4} className="bg-light border-0" value={ptID} onChange={(e) => setPTID(e.target.value)} placeholder="Enter any specific notes about the patient..." />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label className="small font-weight-bold text-muted text-uppercase letter-spacing-1">Ref ID</Form.Label>
              <Form.Control className="bg-light border-0" value={trfType} onChange={(e) => setTrfType(e.target.value)} placeholder="Reference code (optional)" />
            </Form.Group>
          </Col>
          <Col lg={5}>
            <div className="totals-card shadow-sm mt-2">
              <div className="totals-row">
                <span className="totals-label text-muted">Subtotal</span>
                <span className="totals-value">{format_rupees(total)}</span>
              </div>
              <div className="totals-row">
                <span className="totals-label text-danger">Discount (-)</span>
                <div className="d-flex align-items-center">
                  <span className="mr-1 text-danger font-weight-bold">₹</span>
                  <input className="text-right font-weight-bold border-0 bg-transparent text-danger p-0" style={{ width: '80px', outline: 'none' }} type="number" value={less} onChange={(e) => changeLessAmount(e)} />
                </div>
              </div>
              <div className="totals-row">
                <span className="totals-label">Net Total</span>
                <span className="totals-value font-weight-bold">{format_rupees(netAmount)}</span>
              </div>
              <div className="totals-row">
                <span className="totals-label text-success">Advance Paid (-)</span>
                <div className="d-flex align-items-center">
                  <span className="mr-1 text-success font-weight-bold">₹</span>
                  <input className="text-right font-weight-bold border-0 bg-transparent text-success p-0" style={{ width: '80px', outline: 'none' }} type="number" value={advance} onChange={(e) => changeAdvanceAmount(e)} />
                </div>
              </div>
              <div className="totals-row mt-3 pt-3 border-top border-dark">
                <span className="totals-label text-dark font-weight-bold" style={{ fontSize: '1.1rem' }}>BALANCE PAYABLE</span>
                <span className="totals-value payable" style={{ fontSize: '1.6rem' }}>{format_rupees(balancePayable)}</span>
              </div>
            </div>
          </Col>
        </Row>

        <hr className="my-4 border-light" />

        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-2" style={{ gap: '1.2rem' }}>
          {!isMobile && (
            <Button
              variant="light"
              onClick={() => copyBarcode()}
              className="btn-action-premium btn-copy-test"
            >
              <i className="fa fa-clone"></i>
              Copy Testcodes
            </Button>
          )}
          <LoaderButton
            isLoading={isSubmitting}
            disabled={billingJson.length === 1 && (billingJson[0].description === "" || billingJson[0].amount === "" || billingJson[0].amount === 0 || billingJson[0].amount === "0")}
            onClick={() => handleSubmit()}
            className="btn-action-premium btn-generate-trf"
            style={{ minWidth: '350px' }}
          >
            <span>{stateEditRef.current ? "Update TRF" : "Generate TRF"}</span>
            <i className="fa fa-arrow-right ml-2"></i>
          </LoaderButton>
        </div>
      </div>
      <Modal className="mt-5" show={mobilePop} onHide={() => setMobilePop(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Old records found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={12}>
              <Table
                className="custom-card"
                responsive
                hover
                size="sm"
              >
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Age</th>
                    <th>Gender</th>
                  </tr>
                </thead>
                <tbody>
                  {oldRecords && oldRecords.map((o1, index) => {
                    return (
                      <tr key={index} onClick={() => updateOldDetails(o1)} style={{ color: '#1f9bcf', cursor: 'pointer' }}>
                        <td>{o1.patient_name}</td>
                        <td>{o1.patient_age}</td>
                        <td>{o1.patient_gender}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <Modal className="mt-5" show={confirmModal.show} onHide={() => handleConfirmAction(false)} backdrop="static" keyboard={false}>
        <Modal.Header className="bg-light border-bottom-0">
          <Modal.Title className="font-weight-bold text-dark h5">Download TRF</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <p className="text-muted mb-0" style={{ fontSize: '1.1rem' }}>
            Do you want to download the TRF?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0">
          <Button variant="outline-secondary" className="px-4 font-weight-bold" onClick={() => handleConfirmAction(false)} style={{ borderRadius: '8px' }}>
            No
          </Button>
          <Button variant="primary" className="px-4 font-weight-bold" onClick={() => handleConfirmAction(true)} style={{ borderRadius: '8px' }}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Billing;